import { ReactNode } from "react";
import { TFunction } from "i18next";
import { SettingButtonsDisable } from "../types/types";
import { Taxonomy } from "../types/types";
import { ITag } from "@fluentui/react";

export type Column = {
  key: string;
  label: string;
  isVisible: boolean;
  /** If true, this column cannot be dragged (reordered) and it's visibility cannot be changed (disabled checkbox).
   * Fixed columns have to be in a continuous sequence at the beginning of the list.
   */
  isFixed?: boolean;
  /** Should we render this column in the column options list. Defaults to true. */
  isPresentInColumnOptions?: boolean;
  filter?: Filter;
  onDismissCallout?: () => void;
  onOpenCallout?: () => void;
  [key: string]: any;
};

export type TaxonomyKey = any;

export type TaxonomyFilter = {
  type: "taxonomy";
  taxonomyKey: TaxonomyKey;
  value: number[];
  renderLabel?: (props: { taxonomy: Taxonomy; t: TFunction }) => ReactNode;
  /** If true, render a Picker (Select), instead of a checkbox list. Defaults to false */
  isSelect?: boolean;
  /** If true, the picker dropdown is controlled. Applicable only when isSelect is true. */
  isUncontrolledSelect?: boolean;
  /** Array of taxonomy codes to not show in the filter list */
  exclude?: string[];
  customOptionLabel?: (tax: Taxonomy) => string;
  maxOptionsSelect?: number;
  onResolveSuggestions?: (
    filter: string,
    selectedItems?: ITag[]
  ) => ITag[] | Promise<ITag[]>;
};

export type DateFilter = {
  type: "date";
  operator: DateFilterOperator;
  date1: Date | null;
  date2: Date | null;
};

export type NumberFilter = {
  type: "number";
  operator: NumberFilterOperator;
  number1: number | null;
  number2: number | null;
  /** False by default */
  isNegativeAllowed?: boolean;
  max?: number;
  /** True by default */
  includeThousandSeparators?: boolean;
  includeNumber1?: boolean;
  includeNumber2?: boolean;
  /** Float by default */
  format?: "float" | "integer";
};

export type BooleanFilter = {
  type: "boolean";
  value: boolean | null;
  unfilteredValue?: boolean | null;
};

export type TextFilter = {
  type: "text";
  value: string;
};

export type DateFilterOperator =
  | "isAfter"
  | "isBefore"
  | "isExact"
  | "isBetween";

export const dateFilterOperators = [
  "isAfter",
  "isBefore",
  "isExact",
  "isBetween",
];

export type NumberFilterOperator = "gt" | "lt" | "eq" | "between";

export const numberFilterOperators = ["gt", "lt", "eq", "between"];

export type Filter =
  | TaxonomyFilter
  | DateFilter
  | NumberFilter
  | BooleanFilter
  | TextFilter;

export type FilterType = "text" | "number" | "taxonomy" | "date" | "boolean";

export type FilterProps = { [columnKey: string]: any };

/** A single group bucket a column belongs to under a given grouping criterion. */
export type ColumnGroup = { key: string; label: string; order?: number };

/** A "Group by" criterion for the available-fields pane. `groupOf` returns the
 * group a column belongs to, or null to render that column in a flat list
 * (used for the alphabetical/flat grouping). */
export type Grouping = {
  key: string;
  label: string;
  groupOf: (column: Column) => ColumnGroup | null;
};
